import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import { useToast } from '../commons/toast/ToastContext';
import '../../styles/AssignReportModal.css';

const AssignSurveyModal = ({ isOpen, onClose, dogId, onAssigned }) => {
    const { trainer } = useContext(TrainerContext);
    const toast = useToast();
    const { t } = useTranslation(['surveys', 'common']);
    const [surveys, setSurveys] = useState([]);
    const [selectedSurveyId, setSelectedSurveyId] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingSurveys, setLoadingSurveys] = useState(false);

    // Fetch available surveys for the school
    useEffect(() => {
        if (!isOpen || !trainer?.schoolId) return;

        const fetchSurveys = async () => {
            setLoadingSurveys(true);
            try {
                const token = localStorage.getItem('jwt');
                const response = await axios.get(`/api/v1/survey/school/${trainer.schoolId}/all`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSurveys(response.data || []);
            } catch (err) {
                console.error('Error fetching surveys:', err);
                toast.error(t('surveys:fetchListError'), t('common:error'));
            } finally {
                setLoadingSurveys(false);
            }
        };

        fetchSurveys();
    }, [isOpen, trainer?.schoolId, toast]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedSurveyId('');
        }
    }, [isOpen]);

    // Handle form submission
    const handleSubmit = async () => {
        if (!selectedSurveyId || !dogId) {
            toast.warning(t('surveys:selectSurvey'), t('surveys:missingData'));
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');
            await axios.post('/api/v1/survey/assign', {
                surveyId: selectedSurveyId,
                dogId: dogId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(t('surveys:assigned'), t('common:success'));
            onAssigned();
            onClose();
        } catch (err) {
            console.error('Error assigning survey:', err);
            const errorMessage = err.response?.data?.message || t('surveys:assignError');
            toast.error(errorMessage, t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="assign-modal-overlay" onClick={onClose}>
            <div className="assign-modal-content" onClick={e => e.stopPropagation()}>
                <div className="assign-modal-header">
                    <h2>{t('surveys:assignSurvey')}</h2>
                    <button
                        className="assign-modal-close"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="assign-modal-body">
                    <div className="assign-form-group">
                        <label htmlFor="surveySelect">{t('surveys:selectSurveyOption')}</label>
                        {loadingSurveys ? (
                            <p>{t('surveys:loadingSurveys')}</p>
                        ) : surveys.length === 0 ? (
                            <p className="assign-no-results">
                                {t('surveys:noSurveysAvailable')}
                            </p>
                        ) : (
                            <select
                                id="surveySelect"
                                className="assign-form-select"
                                value={selectedSurveyId}
                                onChange={(e) => setSelectedSurveyId(e.target.value)}
                            >
                                <option value="">{t('surveys:selectSurveyOption')}</option>
                                {surveys.map(survey => (
                                    <option key={survey.id} value={survey.id}>
                                        {survey.title} ({t('surveys:questionsCount', { count: survey.questions?.length || 0 })})
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                <div className="assign-modal-footer">
                    <button
                        className="assign-cancel-btn"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {t('common:cancel')}
                    </button>
                    <button
                        className="assign-submit-btn"
                        onClick={handleSubmit}
                        disabled={loading || !selectedSurveyId || surveys.length === 0}
                    >
                        {loading ? t('surveys:assigning') : t('surveys:assign')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignSurveyModal;
